/**
 * notify.js — Lightweight browser notification utility.
 *
 * Uses the standard Notification API (maps to native OS toasts on Tauri for free).
 * Auto-suppresses when the document is focused (user is already looking).
 */

let _permissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';

/**
 * Request notification permission. Call from a user gesture (button click).
 * Returns true if permission was granted.
 */
export async function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') { _permissionGranted = true; return true; }
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  _permissionGranted = result === 'granted';
  return _permissionGranted;
}

/**
 * Send a browser notification.
 * @param {object} opts
 * @param {string} opts.title - Notification title
 * @param {string} opts.body  - Notification body text
 * @param {string} [opts.tag] - Unique tag to prevent duplicates
 * @param {string} [opts.icon] - Icon URL (optional)
 */
export function sendNotification({ title, body, tag, icon }) {
  if (typeof Notification === 'undefined') return;
  if (!_permissionGranted) return;

  // Don't notify if the user is already looking at the app
  if (document.hasFocus()) return;

  try {
    const n = new Notification(title, {
      body,
      tag: tag || undefined,
      icon: icon || '/favicon.svg',
      silent: false,
    });

    // Auto-close after 6 seconds
    setTimeout(() => n.close(), 6000);

    // Focus app window on click
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Notification constructor can throw in some contexts
  }
}

/**
 * Check if notifications are currently permitted.
 */
export function isNotificationPermitted() {
  return _permissionGranted;
}
